import { gql } from "@apollo/client";
import {
  AiRequestFragment,
  useGetAiRequestsQuery,
} from "@src/components/libraryItemDetailPages/course/hooks/useCourseVersionAIRequests.generated";
import { usePollInterval } from "@hooks/usePollInterval";
import { useCallback, useEffect, useMemo } from "react";

type Return = {
  aiRequests: AiRequestFragment[];
};

type Args = {
  courseVersionId?: number;
};

const useCourseVersionAIRequests = (args: Args): Return => {
  const noIncompleteAIRequestsPollInterval = usePollInterval(1000 * 30);
  const someIncompleteAIRequestsPollInterval = usePollInterval(1000);
  const {
    data: data,
    previousData: prevData,
    startPolling,
  } = useGetAiRequestsQuery({
    skip: !args.courseVersionId,
    variables: {
      input: { courseVersionId: args.courseVersionId },
    },
  });
  const aiRequests = useMemo(
    () => data?.GetAIRequests || prevData?.GetAIRequests || [],
    [data?.GetAIRequests, prevData?.GetAIRequests],
  );
  const slowDownAIRequestsPolling = useCallback(() => {
    startPolling(noIncompleteAIRequestsPollInterval);
  }, [noIncompleteAIRequestsPollInterval, startPolling]);
  const speedUpAIRequestsPolling = useCallback(() => {
    startPolling(someIncompleteAIRequestsPollInterval);
  }, [someIncompleteAIRequestsPollInterval, startPolling]);
  useEffect(() => {
    const incompleteAiRequests = aiRequests.filter(
      (aiRequest) => !aiRequest.aiEditPromptRequest?.generationCompletedAt,
    );
    if (incompleteAiRequests.length === 0) {
      slowDownAIRequestsPolling();
    } else {
      speedUpAIRequestsPolling();
    }
  }, [
    aiRequests,
    noIncompleteAIRequestsPollInterval,
    someIncompleteAIRequestsPollInterval,
    slowDownAIRequestsPolling,
    speedUpAIRequestsPolling,
    startPolling,
  ]);
  return useMemo(() => {
    return {
      aiRequests,
    };
  }, [aiRequests]);
};

gql`
  mutation EditPromptWithAI($input: EditPromptWithAIInput!) {
    editPromptWithAI(input: $input) {
      success
      error {
        code
      }
      aiRequest {
        ...AIRequestThin
      }
    }
  }

  fragment AIRequest on AIRequest {
    ...AIRequestThin
    requestText
    aiEditPromptRequest {
      ...AIEditPromptRequest
    }
  }

  fragment MediaSuggestion on MediaSuggestion {
    id
    media {
      id
      mediaUrls {
        ...TranslationSet
      }
      imageUrls {
        ...ImageUrls
      }
    }
  }

  fragment AIResponseOption on AIResponseOption {
    id
    answer {
      ...TranslationSet
    }
    index
    isCorrect
  }

  fragment AIEditPromptRequest on AIEditPromptRequest {
    id
    prompt {
      id
      uuid
    }
    response
    requestType
    mediaSuggestions {
      ...MediaSuggestion
    }
    aiResponseOptions {
      ...AIResponseOption
    }
    generationStartedAt
    generationCompletedAt
    generationErroredAt
  }

  query GetAIRequests($input: GetAIRequestsInput!) {
    GetAIRequests(input: $input) {
      ...AIRequest
    }
  }

  mutation ResolveAIRequest($input: ResolveAIRequestInput!) {
    resolveAIRequest(input: $input) {
      success
      error {
        code
      }
      aiRequest {
        ...AIRequestThin
      }
    }
  }
`;

export default useCourseVersionAIRequests;
